const transferSteps = [
  'Create a backup using the built-in backup feature on your current computer',
  'Install Family Tree Maker 2019 on your new computer',
  'Restore your backup file using the restore function',
  'Confirm that all media files and data transferred correctly',
  'Activate your license on the new device',
];

const keyFeatures = [
  { label: 'Pedigree View', desc: 'Classic ancestry tree layout' },
  { label: 'Family View', desc: 'Complete family units and relationships' },
  { label: 'Name List', desc: 'Quick access to everyone in your tree' },
  { label: 'Individual Details', desc: 'Full profile for each person' },
  { label: 'Media Gallery', desc: 'Photos and documents in one place' },
  { label: 'Research Log', desc: 'Track sources and findings' },
];

export default function AdditionalSections() {
  return (
    <>
      <section className="py-20 bg-[#e8f5e9]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2d2520] mb-6">
            Smart Solutions for Your Family History
          </h2>
          <div className="section-divider w-24 mx-auto mb-10" />
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#4a4038] text-lg leading-relaxed mb-4">
              Moving between Windows and Mac, or upgrading to a new computer? Family Tree Maker makes the switch simple.
              Our migration tools transfer your tree, media, and notes without losing a single detail.
            </p>
            <p className="text-[#5a534a] text-base leading-relaxed">
              Pick up right where you left off—no matter which platform you choose—and keep building your family story.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2d2520] mb-6">
            Transfer FTM to a New Computer
          </h2>
          <div className="section-divider w-24 mx-auto mb-10" />
          <div className="max-w-2xl mx-auto">
            <p className="text-[#4a4038] text-lg leading-relaxed mb-8 text-center">
              Moving Family Tree Maker 2019 to another computer is straightforward. Follow these steps to migrate your data safely.
            </p>
            <ol className="space-y-4 text-[#4a4038]">
              {transferSteps.map((step, i) => (
                <li key={step} className="flex gap-4 items-start">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#22c55e] text-white flex items-center justify-center font-semibold text-sm">
                    {i + 1}
                  </span>
                  <span className="pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#e8f5e9]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2d2520] mb-6">
            What is Family Tree Maker?
          </h2>
          <div className="section-divider w-24 mx-auto mb-10" />
          <div className="max-w-3xl mx-auto text-center space-y-5">
            <p className="text-[#4a4038] text-lg leading-relaxed">
              Family Tree Maker helps you discover, preserve, and share your family history. It brings together
              research tools, clear organization, and beautiful charting—whether you&apos;re a beginner or an experienced genealogist.
            </p>
            <p className="text-[#5a534a] text-base leading-relaxed">
              Build detailed trees, attach photos and documents, create reports, and sync with FamilySearch—all in one place.
              Smart matching, color coding, and cloud sync make research smoother and more rewarding.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2d2520] mb-6">
            Family Tree Maker Upgrade
          </h2>
          <div className="section-divider w-24 mx-auto mb-10" />
          <div className="max-w-3xl mx-auto text-center space-y-5">
            <p className="text-[#4a4038] text-lg leading-relaxed">
              Family Tree Maker gives you the tools genealogists rely on. Upgrading unlocks better charting,
              improved media management, and tighter integration with FamilySearch and other research sources.
            </p>
            <p className="text-[#5a534a] text-base leading-relaxed">
              The upgrade preserves your existing data and adds new reporting options, performance improvements,
              and access to the latest features—so you can keep building your family story with confidence.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#e8f5e9]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2d2520] mb-4">
            Key Features at a Glance
          </h2>
          <p className="text-base text-[#5a534a] text-center mb-12 max-w-2xl mx-auto">
            Everything you need to research, organize, and share your family history.
          </p>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {keyFeatures.map((feature) => (
                <div
                  key={feature.label}
                  className="flex items-start gap-4 p-4 rounded-lg bg-white/70 border border-[#e8e2d8]"
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-[#22c55e] rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-[#2d2520]">{feature.label}</p>
                    <p className="text-sm text-[#5a534a]">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[#5a534a] text-center text-sm max-w-2xl mx-auto">
              From your first ancestor to a full family network, Family Tree Maker supports every step of your genealogy journey.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
